// src/pages/utils/exportExcel.js
import * as XLSX from 'xlsx';

const DISCLAIMER = `This information provided for informational purposes only does not constitute a recommendation, solicitation or municipal advisory service, and is not a guarantee of future performance. See more information at greenlandrm.com/disclosures.`;
const MEMBERSHIP = 'Member NFA | SEC/MSRB Registered';
const COPYRIGHT = `©${new Date().getFullYear()} Greenland Risk Management LLC. All rights reserved.`;

// Use browser's local timezone
function formatCreatedStamp(date = new Date()) {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(date);
}

function toExcelDateOnly(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toExcelDateTime(str) {
  if (!str) return null;
  const normalized = String(str).replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isTimestampHeader(h) {
  const s = String(h).toLowerCase();
  return s.includes('timestamp') || s.includes('created') || s.includes('updated');
}

function isDateHeader(h) {
  const s = String(h).toLowerCase();
  return (
    s === 'date' ||
    s.endsWith(' date') ||
    s.includes('as of') ||
    s.includes('valuation date') ||
    s.includes('trade date')
  );
}

/**
 * Excel export: Table first, then chart data, KPIs vertical
 */
export default function exportToExcel({
  sheetName = 'Sheet1',
  fileName = 'Export.xlsx',
  headers,
  rows,
  title = '',
  subtitle = '',
  createdStamp = null,
  summary = null,     // { totalVal, totalNotional, totalPv01 }
  chartSeries = null, // [{ date, valuation, notional }]
}) {
  if (!rows || rows.length === 0) return;

  const ws = XLSX.utils.aoa_to_sheet([]);
  const stamp = createdStamp ? formatCreatedStamp(createdStamp) : formatCreatedStamp();

  /* --------------------------
   * HEADER BLOCK
   * -------------------------- */
  const headerRows = [];

  if (title) headerRows.push([title]);
  if (subtitle) headerRows.push([subtitle]);
  headerRows.push([]);

  if (summary) {
    headerRows.push(['Summary KPIs']);
    headerRows.push(['Total Value', summary.totalVal ?? '']);
    headerRows.push(['Total Notional', summary.totalNotional ?? '']);
    headerRows.push(['Total PV01', summary.totalPv01 ?? '']);
    headerRows.push([]);
  }

  XLSX.utils.sheet_add_aoa(ws, headerRows, { origin: 'A1' });

  /* --------------------------
   * MAIN DATA TABLE
   * -------------------------- */
  const tableStartRow = headerRows.length + 1;

  XLSX.utils.sheet_add_json(ws, rows, {
    header: headers,
    origin: `A${tableStartRow}`,
    skipHeader: false,
  });

  const tableEndRow = tableStartRow + rows.length;

  /* --------------------------
   * CHART DATA AFTER THE TABLE
   * -------------------------- */
  let afterTableRow = tableEndRow + 2;

  if (Array.isArray(chartSeries) && chartSeries.length > 0) {
    const chartBlock = [];
    chartBlock.push(['Historical Portfolio (chart data)']);
    chartBlock.push(['Date', 'Value', 'Notional']);

    chartSeries.forEach((pt) => {
      chartBlock.push([
        pt.date || '',
        pt.valuation != null ? Number(pt.valuation) : '',
        pt.notional != null ? Number(pt.notional) : '',
      ]);
    });

    chartBlock.push([]);

    XLSX.utils.sheet_add_aoa(ws, chartBlock, { origin: `A${afterTableRow}` });
    afterTableRow += chartBlock.length;
  }

  /* --------------------------
   * AUTO COLUMN WIDTHS
   * -------------------------- */
  ws['!cols'] = headers.map((h) => {
    if (isTimestampHeader(h)) return { wch: 22 };
    if (isDateHeader(h)) return { wch: 12 };

    const maxLen = Math.max(
      String(h || '').length,
      ...rows.map((r) => (r[h] == null ? 0 : String(r[h]).length)),
    );

    return { wch: Math.min(Math.max(maxLen + 2, 10), 40) };
  });

  /* --------------------------
   * DATE FORMATTING
   * -------------------------- */
  const dataStartRow = tableStartRow + 1;

  headers.forEach((h, colIdx) => {
    const isTs = isTimestampHeader(h);
    const isDt = isDateHeader(h);
    if (!isTs && !isDt) return;

    const colLetter = XLSX.utils.encode_col(colIdx);

    for (let row = dataStartRow; row <= tableEndRow; row++) {
      const cellRef = colLetter + row;
      const cell = ws[cellRef];
      if (!cell) continue;

      if (cell.v instanceof Date) {
        cell.t = 'd';
        cell.z = isTs ? 'm/d/yyyy h:mm:ss AM/PM' : 'm/d/yyyy';
        continue;
      }

      if (typeof cell.v === 'string') {
        const v = cell.v.trim();
        if (isTs) {
          const d = toExcelDateTime(v);
          if (d) {
            cell.v = d;
            cell.t = 'd';
            cell.z = 'm/d/yyyy h:mm:ss AM/PM';
            continue;
          }
        } else if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
          const d = toExcelDateOnly(v);
          if (d) {
            cell.v = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            cell.t = 'd';
            cell.z = 'm/d/yyyy';
            continue;
          }
        }
      }
    }
  });

  /* --------------------------
   * FOOTER
   * -------------------------- */
  const footerStart = afterTableRow + 2;
  const footerRows = [
    [`Report created ${stamp}`],
    [DISCLAIMER],
    [MEMBERSHIP],
    [COPYRIGHT],
  ];
  XLSX.utils.sheet_add_aoa(ws, footerRows, { origin: `A${footerStart}` });

  /* --------------------------
   * FINAL EXPORT
   * -------------------------- */
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
}
